/**
 * Panel focus requests (`panel.focus`, SPEC §4.3): the Console on `~`, and the
 * tour's target panels.
 *
 * The command only records *which* panel should come forward; the dockview
 * layout reacts to it. The command layer never touches the docking library,
 * so a future console or agent can focus panels the same way.
 */
import { parsePayload } from "../../commands/payload";
import type { CommandDefinition } from "../../commands/types";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import { PANEL_IDS } from "./layout";
import type { PanelId } from "./layout";

export interface FocusRequest {
  readonly panel: PanelId | null;
}

export class PanelFocus implements ReadableStore<FocusRequest> {
  private readonly store: VersionedStore<FocusRequest>;

  constructor(scheduler: ChangeScheduler) {
    this.store = new VersionedStore<FocusRequest>(scheduler, { panel: null });
  }

  readonly subscribe = (listener: () => void) => this.store.subscribe(listener);
  readonly getSnapshot = (): FocusRequest => this.store.getSnapshot();

  /** @internal For `panel.focus` only. A new object each time, so repeats still notify. */
  request(panel: PanelId): void {
    this.store.publish({ panel });
  }
}

export function panelFocusCommands(focus: PanelFocus): CommandDefinition[] {
  const focusPanel: CommandDefinition<{ panel: PanelId }, undefined> = {
    type: "panel.focus",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { panel: "string" });
      if (!shape.ok) return shape;
      if (!(PANEL_IDS as readonly string[]).includes(shape.value.panel)) {
        return { ok: false, error: `Unknown panel '${shape.value.panel}'` };
      }
      return { ok: true, value: { panel: shape.value.panel as PanelId } };
    },
    do: ({ panel }) => {
      focus.request(panel);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ panel }) => `Focus ${panel}`,
    quiet: true,
  };
  return [focusPanel] as CommandDefinition[];
}
